import React, { Component } from 'react';
import axios from 'axios';
import logo from './img/sidebar/logo.png';
import './css/admin-support.css';

const STATUSES = ['open', 'in_progress', 'pending_user', 'resolved', 'closed'];
const PRIORITIES = ['Low', 'Medium', 'High', 'Urgent'];
const CATEGORIES = ['Question', 'Bug', 'Feature Request', 'Billing', 'Account', 'Other'];

class AdminPortal extends Component {
    state = {
        authenticated: false,
        checkingSession: true,
        email: '',
        password: '',
        loginError: '',
        admin: null,
        tickets: [],
        statusFilter: 'all',
        loading: false,
        selectedTicket: null,
        messages: [],
        reply: '',
        saving: false,
        error: ''
    };

    componentDidMount() {
        this.validateExistingSession();
    }

    adminHeaders = () => ({
        Authorization: localStorage.getItem('AdminAuthToken') || '',
        RefreshToken: localStorage.getItem('AdminRefreshToken') || ''
    });

    persistAdminTokens = (response) => {
        if (response?.headers?.authtoken && response?.headers?.refreshtoken) {
            localStorage.setItem('AdminAuthToken', `Bearer ${response.headers.authtoken}`);
            localStorage.setItem('AdminRefreshToken', response.headers.refreshtoken);
        }
    };

    validateExistingSession = () => {
        if (!localStorage.getItem('AdminAuthToken')) {
            this.setState({ checkingSession: false });
            return;
        }

        axios.get('/api/admin/session', { headers: this.adminHeaders() })
            .then((response) => {
                this.persistAdminTokens(response);
                this.setState({ authenticated: true, checkingSession: false, admin: response.data.admin }, this.loadTickets);
            })
            .catch(() => {
                this.clearAdminSession();
                this.setState({ checkingSession: false });
            });
    };

    login = (event) => {
        event.preventDefault();
        this.setState({ loading: true, loginError: '' });

        axios.post('/api/login', { email: this.state.email.trim(), password: this.state.password })
            .then((loginResponse) => {
                const token = loginResponse.data?.token;
                const refreshToken = loginResponse.data?.refreshToken;
                if (!token || !refreshToken) throw new Error('Authentication tokens were not returned.');

                localStorage.setItem('AdminAuthToken', `Bearer ${token}`);
                localStorage.setItem('AdminRefreshToken', refreshToken);
                return axios.get('/api/admin/session', { headers: this.adminHeaders() });
            })
            .then((response) => {
                this.persistAdminTokens(response);
                this.setState({ authenticated: true, admin: response.data.admin, loading: false, password: '' }, this.loadTickets);
            })
            .catch((error) => {
                this.clearAdminSession();
                this.setState({ loading: false, loginError: error?.response?.status === 403 ? 'Administrator access denied.' : 'Unable to sign in. Check your credentials.' });
            });
    };

    clearAdminSession = () => {
        localStorage.removeItem('AdminAuthToken');
        localStorage.removeItem('AdminRefreshToken');
    };

    logout = () => {
        this.clearAdminSession();
        this.setState({ authenticated: false, admin: null, tickets: [], selectedTicket: null, messages: [], email: '', password: '' });
    };

    handleAdminError = (error, fallback) => {
        console.log(error);
        if (error?.response?.status === 401 || error?.response?.status === 403) {
            this.logout();
            this.setState({ loginError: 'Your administrator session has expired or access was revoked.' });
            return;
        }
        this.setState({ error: error?.response?.data?.error || fallback });
    };

    loadTickets = () => {
        this.setState({ loading: true, error: '' });
        axios.get('/api/admin/support/tickets', { headers: this.adminHeaders(), params: { status: this.state.statusFilter } })
            .then((response) => {
                this.persistAdminTokens(response);
                this.setState({ tickets: response.data?.tickets || [], loading: false });
            })
            .catch((error) => {
                this.setState({ loading: false });
                this.handleAdminError(error, 'Unable to load support tickets.');
            });
    };

    changeFilter = (event) => {
        this.setState({ statusFilter: event.target.value }, this.loadTickets);
    };

    openTicket = (ticket) => {
        this.setState({ loading: true, error: '', reply: '' });
        axios.get(`/api/admin/support/tickets/${ticket.id}`, { headers: this.adminHeaders() })
            .then((response) => {
                this.persistAdminTokens(response);
                this.setState({ selectedTicket: response.data.ticket, messages: response.data.messages || [], loading: false });
                this.loadTickets();
            })
            .catch((error) => {
                this.setState({ loading: false });
                this.handleAdminError(error, 'Unable to load support ticket.');
            });
    };

    updateTicket = (updates) => {
        if (!this.state.selectedTicket) return;
        this.setState({ saving: true, error: '' });
        axios.patch(`/api/admin/support/tickets/${this.state.selectedTicket.id}`, updates, { headers: this.adminHeaders() })
            .then((response) => {
                this.persistAdminTokens(response);
                this.setState({ selectedTicket: response.data.ticket, saving: false });
                this.loadTickets();
            })
            .catch((error) => {
                this.setState({ saving: false });
                this.handleAdminError(error, 'Unable to update ticket.');
            });
    };

    sendReply = (event) => {
        event.preventDefault();
        if (!this.state.selectedTicket || !this.state.reply.trim()) return;

        this.setState({ saving: true, error: '' });
        axios.post(`/api/admin/support/tickets/${this.state.selectedTicket.id}/replies`, { message: this.state.reply }, { headers: this.adminHeaders() })
            .then((response) => {
                this.persistAdminTokens(response);
                this.setState({ selectedTicket: response.data.ticket, messages: response.data.messages || [], reply: '', saving: false });
                this.loadTickets();
            })
            .catch((error) => {
                this.setState({ saving: false });
                this.handleAdminError(error, 'Unable to send reply.');
            });
    };

    formatDate = (value) => {
        if (!value) return '';
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
    };

    prettyStatus = (status) => String(status || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

    renderLogin() {
        return (
            <div className="admin-login-page">
                <form className="admin-login-card" onSubmit={this.login}>
                    <img src={logo} alt="GoRepa" />
                    <h1>GoRepa Admin</h1>
                    <p>Administrator access only</p>
                    {this.state.loginError && <div className="admin-error">{this.state.loginError}</div>}
                    <label>Email</label>
                    <input type="email" autoComplete="username" value={this.state.email} onChange={(e) => this.setState({ email: e.target.value })} required />
                    <label>Password</label>
                    <input type="password" autoComplete="current-password" value={this.state.password} onChange={(e) => this.setState({ password: e.target.value })} required />
                    <button type="submit" disabled={this.state.loading}>{this.state.loading ? 'Signing In...' : 'Sign In'}</button>
                </form>
            </div>
        );
    }

    render() {
        if (this.state.checkingSession) return <div className="admin-login-page"><div className="admin-loading">Checking administrator session...</div></div>;
        if (!this.state.authenticated) return this.renderLogin();

        return (
            <div className="gorepa-admin-shell">
                <aside className="gorepa-admin-sidebar">
                    <div className="gorepa-admin-brand"><img src={logo} alt="GoRepa"/><div><b>GoRepa Admin</b><span>Operations</span></div></div>
                    <button className="gorepa-admin-nav active"><i className="far fa-life-ring"></i> Support Tickets</button>
                    <div className="gorepa-admin-sidebar-bottom">
                        <div className="gorepa-admin-user">{this.state.admin?.name}<span>{this.state.admin?.email}</span></div>
                        <button className="gorepa-admin-logout" onClick={this.logout}><i className="fas fa-sign-out-alt"></i> Log Out</button>
                    </div>
                </aside>

                <main className="gorepa-admin-content">
                    <div className="gorepa-admin-header">
                        <div><h1>Support Tickets</h1><p>Manage customer questions, issues, and requests.</p></div>
                        <select value={this.state.statusFilter} onChange={this.changeFilter}>
                            <option value="all">All statuses</option>
                            {STATUSES.map((status) => <option key={status} value={status}>{this.prettyStatus(status)}</option>)}
                        </select>
                    </div>

                    {this.state.error && <div className="admin-error">{this.state.error}</div>}

                    <div className={`gorepa-admin-workspace ${this.state.selectedTicket ? 'with-detail' : ''}`}>
                        <section className="gorepa-admin-ticket-list">
                            {this.state.loading && this.state.tickets.length === 0 ? <div className="admin-empty">Loading...</div> : this.state.tickets.length === 0 ? <div className="admin-empty">No tickets match this filter.</div> : this.state.tickets.map((ticket) => (
                                <button className={`admin-ticket-card ${this.state.selectedTicket?.id === ticket.id ? 'selected' : ''}`} key={ticket.id} onClick={() => this.openTicket(ticket)}>
                                    <div className="admin-ticket-card-top"><b>{ticket.title}</b><span className={`admin-status status-${ticket.status}`}>{this.prettyStatus(ticket.status)}</span></div>
                                    <div className="admin-ticket-customer">{ticket.userName} · {ticket.userEmail}</div>
                                    <div className="admin-ticket-card-bottom"><span>{ticket.ticketNumber}</span><span>{ticket.category}</span><span>{ticket.priority}</span><span>{this.formatDate(ticket.updatedAt)}</span>{Number(ticket.adminUnreadCount || 0) > 0 && <strong>{ticket.adminUnreadCount} new</strong>}</div>
                                </button>
                            ))}
                        </section>

                        {this.state.selectedTicket && (
                            <section className="gorepa-admin-ticket-detail">
                                <button className="admin-detail-close" onClick={() => this.setState({ selectedTicket: null, messages: [] })}>×</button>
                                <h2>{this.state.selectedTicket.title}</h2>
                                <div className="admin-detail-subtitle">{this.state.selectedTicket.ticketNumber} · {this.state.selectedTicket.userName} · <a href={`mailto:${this.state.selectedTicket.userEmail}`}>{this.state.selectedTicket.userEmail}</a></div>

                                <div className="admin-ticket-controls">
                                    <label>Status<select value={this.state.selectedTicket.status} onChange={(e) => this.updateTicket({ status: e.target.value })}>{STATUSES.map((status) => <option key={status} value={status}>{this.prettyStatus(status)}</option>)}</select></label>
                                    <label>Priority<select value={this.state.selectedTicket.priority} onChange={(e) => this.updateTicket({ priority: e.target.value })}>{PRIORITIES.map((priority) => <option key={priority} value={priority}>{priority}</option>)}</select></label>
                                    <label>Category<select value={this.state.selectedTicket.category} onChange={(e) => this.updateTicket({ category: e.target.value })}>{CATEGORIES.map((category) => <option key={category} value={category}>{category}</option>)}</select></label>
                                </div>

                                <div className="admin-conversation">
                                    {this.state.messages.map((message) => (
                                        <div className={`admin-message ${message.authorType}`} key={message.id}>
                                            <div><b>{message.authorType === 'admin' ? message.authorName || 'GoRepa Support' : message.authorName}</b><span>{this.formatDate(message.createdAt)}</span></div>
                                            <p>{message.body}</p>
                                        </div>
                                    ))}
                                </div>

                                {this.state.selectedTicket.status !== 'closed' && (
                                    <form onSubmit={this.sendReply} className="admin-reply-form">
                                        <label>Reply to customer</label>
                                        <textarea value={this.state.reply} onChange={(e) => this.setState({ reply: e.target.value })} placeholder="Type your response..." />
                                        <button disabled={this.state.saving || !this.state.reply.trim()}>{this.state.saving ? 'Sending...' : 'Send Reply'}</button>
                                    </form>
                                )}
                            </section>
                        )}
                    </div>
                </main>
            </div>
        );
    }
}

export default AdminPortal;
